import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import LanguageToggle from "@/components/LanguageToggle";
import { LogoIcon, LogoWordmark } from "@/components/Logo";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import { toast } from "sonner";
import { Loader2, RefreshCw, Wifi, KeyRound, LifeBuoy, RotateCcw, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";
import { refreshApplication } from "@/lib/appUpdate";

// Safe diagnostic: a code support can look up. Never logs passwords, tokens or cookies.
function logDiag(category) {
  const code = `LG-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
  try {
    const log = JSON.parse(localStorage.getItem("bp_diag_log") || "[]");
    log.push({ code, ts: new Date().toISOString(), category });
    localStorage.setItem("bp_diag_log", JSON.stringify(log.slice(-10)));
  } catch (e) { /* ignore */ }
  console.info(`[BuildProject360] login diagnostic ${code} (${category})`);
  return code;
}

export default function Login() {
  const { t } = useTranslation();
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null); // { message, code }
  const [showPw, setShowPw] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const capsCheck = (e) => { if (e.getModifierState) setCapsOn(e.getModifierState("CapsLock")); };
  useSeo({ title: t("seo_login_title"), description: t("seo_login_desc") });

  // Already signed in with a valid session → never show the login form.
  if (user) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e?.preventDefault();
    if (!email || !password || busy) return;
    setBusy(true);
    setFailure(null);
    const res = await login(email.trim().toLowerCase(), password);
    setBusy(false);
    if (res.ok) {
      trackEvent("login_completed");
      toast.success(t("toast_welcome_back"));
      const next = new URLSearchParams(window.location.search).get("next");
      navigate(next && next.startsWith("/app") ? next : "/app");
      return;
    }
    const msg = String(res.error || "unknown");
    const category = /network|timeout|fetch|failed to fetch/i.test(msg) ? "network" : "auth";
    setFailure({ message: msg, code: logDiag(category) });
  };

  const checkConnection = async () => {
    try {
      await api.get("/health");
      toast.success(t("login_online"));
    } catch (e) {
      toast.error(t("login_offline"));
    }
  };

  const forgot = async () => {
    if (!email) { toast.error(t("auth_email")); return; }
    try { await api.post("/auth/forgot-password", { email }); } catch (e) { /* never reveal account existence */ }
    toast.success(t("forgot_sent"));
  };

  const contactSupport = () => {
    const subject = encodeURIComponent("BuildProject360 login help");
    const body = encodeURIComponent(`Support code: ${failure?.code || "n/a"}\n\nWhat happened:\n`);
    window.location.href = `mailto:admin@buildproject360.com?subject=${subject}&body=${body}`;
  };

  const RecoveryBtn = ({ icon: Icon, label, onClick, testid, wide }) => (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-full border border-slate-600 bg-slate-800/80 px-3 text-xs font-bold text-slate-100 transition-colors hover:bg-slate-700 active:scale-95 ${wide ? "col-span-2" : ""}`}>
      <Icon className="h-4 w-4 text-brand" /> {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-slateink bf-grain flex flex-col">
      <div className="flex items-center justify-between px-5 py-5">
        <Link to="/" className="flex items-center gap-2 text-white font-bold" data-testid="link-home">
          <LogoIcon size={36} />
          <LogoWordmark dark className="text-base" />
        </Link>
        <LanguageToggle />
      </div>
      <div className="flex-1 flex items-center justify-center px-5 py-8">
        <div className="w-full max-w-sm space-y-4">
          <form onSubmit={submit} className="space-y-5 rounded-2xl border border-slate-800 bg-slate-900/70 p-6 md:p-8">
            <h1 className="font-heading text-2xl font-extrabold text-white">{t("auth_login_title")}</h1>
            <div className="space-y-3">
              <label htmlFor="login-email" className="block text-xs font-semibold uppercase tracking-widest text-slate-400">{t("auth_email")}</label>
              <Input id="login-email" name="email" autoComplete="email" inputMode="email" data-testid="input-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 bg-slate-950 border-slate-700 text-white" />
            </div>
            <div className="space-y-3">
              <label htmlFor="login-password" className="block text-xs font-semibold uppercase tracking-widest text-slate-400">{t("auth_password")}</label>
              <div className="relative">
                <Input id="login-password" name="current-password" autoComplete="current-password" data-testid="input-password" type={showPw ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)} onKeyDown={capsCheck} onKeyUp={capsCheck}
                  className="h-12 bg-slate-950 border-slate-700 text-white pr-12" />
                <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? t("auth_pw_hide") : t("auth_pw_show")} data-testid="login-toggle-pw"
                  className="absolute right-1 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full text-slate-400 hover:text-white transition-colors">
                  {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {capsOn && (
                <div className="text-xs font-semibold text-amber-400" data-testid="login-caps-warning" role="status">{t("auth_caps_lock")}</div>
              )}
              <div className="text-right">
                <button type="button" onClick={forgot} data-testid="link-forgot" className="text-xs font-semibold text-brand hover:underline min-h-[32px]">{t("forgot_password")}</button>
              </div>
            </div>
            <button type="submit" data-testid="btn-signin" disabled={busy} className="bf-thumb-cta inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand font-bold text-white bf-brand-shadow transition-transform active:scale-95 disabled:opacity-60">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("action_signin")}
            </button>
            <div className="flex items-center gap-3" data-testid="auth-divider">
              <div className="h-px flex-1 bg-slate-700" />
              <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{t("auth_or")}</span>
              <div className="h-px flex-1 bg-slate-700" />
            </div>
            <GoogleAuthButton testid="google-signin-btn" />
            <div className="text-sm text-slate-400 text-center">
              {t("auth_no_account")} <Link to="/signup" className="text-brand font-semibold hover:underline" data-testid="link-signup">{t("action_create_account")}</Link>
            </div>
          </form>

          {failure && (
            <div data-testid="login-recovery" className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 space-y-3" role="alert">
              <div>
                <div className="text-sm font-bold text-amber-200">{t("login_trouble_title")}</div>
                <div className="mt-1 text-xs text-slate-300 break-words" data-testid="login-error-text">{failure.message}</div>
                <div className="mt-1 text-[11px] text-slate-400">
                  {t("login_diag_code")}: <span className="font-mono text-slate-300" data-testid="login-diag-code">{failure.code}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <RecoveryBtn icon={RotateCcw} label={t("login_try_again")} onClick={() => submit()} testid="login-try-again" />
                <RecoveryBtn icon={Wifi} label={t("login_check_connection")} onClick={checkConnection} testid="login-check-connection" />
                <RecoveryBtn icon={RefreshCw} label={t("login_refresh_app")} onClick={refreshApplication} testid="login-refresh-app" />
                <RecoveryBtn icon={KeyRound} label={t("login_reset_password")} onClick={forgot} testid="login-reset-password" />
                <RecoveryBtn icon={LifeBuoy} label={t("login_contact_support")} onClick={contactSupport} testid="login-contact-support" wide />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
